"use client";

import React, { useState, useEffect } from "react";
import { Wifi, Briefcase, Users, Star, ShieldCheck, Thermometer } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const fleetData = [{
  id: 1,
  name: "Luxury Saloon",
  model: "Mercedes-Benz S-Class",
  description: "The ultimate expression of automotive luxury. With its serene cabin, exquisite materials, and advanced suspension, the S-Class provides a sanctuary of calm for the discerning executive traveler.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/mercedes_s_class_obsidian.png",
  specs: [{
    icon: Users,
    label: "3 Passengers"
  }, {
    icon: Briefcase,
    label: "2 Large Bags"
  }, {
    icon: Wifi,
    label: "High-Speed Wi-Fi"
  }, {
    icon: Star,
    label: "Massage Seats"
  }, {
    icon: ShieldCheck,
    label: "Executive Pack"
  }, {
    icon: Thermometer,
    label: "4-Zone Climate"
  }]
}, {
  id: 2,
  name: "Executive Saloon",
  model: "BMW 7 Series",
  description: "A masterpiece of engineering and comfort. The 7 Series combines cutting-edge technology with a whisper-quiet interior, ensuring a seamless transition between the office and the road.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/bmw_7_series_onyx.png",
  specs: [{
    icon: Users,
    label: "3 Passengers"
  }, {
    icon: Briefcase,
    label: "2 Large Bags"
  }, {
    icon: Wifi,
    label: "Onboard Wi-Fi"
  }, {
    icon: Star,
    label: "Panoramic Roof"
  }, {
    icon: ShieldCheck,
    label: "Active Safety"
  }, {
    icon: Thermometer,
    label: "Precision A/C"
  }]
}, {
  id: 3,
  name: "Prestige SUV",
  model: "Cadillac Escalade",
  description: "Unrivaled presence and spaciousness. The Escalade offers a commanding view of the road and a vast, premium cabin for groups who refuse to compromise on style or scale.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/cadillac_escalade_platinum.png",
  specs: [{
    icon: Users,
    label: "6 Passengers"
  }, {
    icon: Briefcase,
    label: "6 Large Bags"
  }, {
    icon: Wifi,
    label: "Gigabit Wi-Fi"
  }, {
    icon: Star,
    label: "Premium Audio"
  }, {
    icon: ShieldCheck,
    label: "Armored Glass"
  }, {
    icon: Thermometer,
    label: "Triple-Zone"
  }]
}];
export default function PremierFleetPreview() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  return <section data-section-id="62126" id="our-fleet" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-primary font-default font-medium tracking-widest uppercase text-sm mb-3">
              The Selection
            </h2>
            <h3 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4">
              Pinnacle of <span className="text-primary">Automotive Mastery</span>
            </h3>
            <p className="text-muted-foreground text-lg">
              Experience the ultimate in comfort and style with our meticulously maintained fleet of executive vehicles.
            </p>
          </div>
          
          <div className="hidden md:flex gap-2">
          </div>
        </div>

        <Carousel setApi={setApi} className="w-full" opts={{
        align: "start",
        loop: true
      }}>
          <CarouselContent className="-ml-4">
            {fleetData.map((vehicle, index) => <CarouselItem key={vehicle.id} className="pl-4 basis-full" data-index={index}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-card border-t border-primary/20 backdrop-blur-sm rounded-2xl overflow-hidden p-6 lg:p-10 transition-transform duration-300 hover:scale-[1.02] shadow-2xl">
                  
                  <div className="lg:col-span-7 relative group overflow-hidden rounded-xl aspect-[16/10] lg:aspect-auto lg:h-[500px]">
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent z-10" />
                    <img src={vehicle.image} alt={`${vehicle.name} - ${vehicle.model}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute bottom-6 left-6 z-20">
                      <p className="text-primary/80 text-sm font-default font-medium tracking-wider uppercase mb-1">
                        {vehicle.name}
                      </p>
                      <h4 className="text-3xl font-default font-bold text-foreground">
                        {vehicle.model}
                      </h4>
                    </div>
                  </div>

                  <div className="lg:col-span-5 flex flex-col justify-center space-y-8">
                    <div>
                      <h4 className="text-2xl font-default font-semibold text-foreground mb-4">
                        Vehicle Overview
                      </h4>
                      <p className="text-muted-foreground leading-relaxed">
                        {vehicle.description}
                      </p>
                    </div>

                    <div>
                      <h5 className="text-sm font-default font-medium text-primary uppercase tracking-wider mb-4">
                        Specifications & Amenities
                      </h5>
                      <div className="flex flex-wrap gap-3">
                        {vehicle.specs.map((spec, i) => <Badge key={i} variant="outline" className="py-2 px-3 border-border bg-secondary/50 text-foreground font-mono hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-colors gap-2" data-index={i}>
                            <spec.icon className="size-3.5" />
                            {spec.label}
                          </Badge>)}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-border flex flex-col sm:flex-row gap-4">
                      <Button asChild size="lg" className="bg-transparent border border-primary text-primary hover:bg-primary/10 hover:shadow-[0_0_15px_rgba(var(--primary),0.3)] w-full sm:w-auto">
                        <Link to="/ServicesPage">
                          Book This Vehicle
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-secondary w-full sm:w-auto">
                        <Link to="/ReachUsPage">
                          Request Quote
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>
          
          <div className="flex justify-center mt-8 gap-4">
            <CarouselPrevious className="static translate-y-0 translate-x-0 bg-transparent border-border text-foreground hover:bg-primary hover:border-primary hover:text-primary-foreground" />
            <div className="flex items-center gap-2 px-4">
               <span className="text-sm font-default font-medium text-muted-foreground">
                 {current} <span className="mx-1 text-primary/20">/</span> {count}
               </span>
            </div>
            <CarouselNext className="static translate-y-0 translate-x-0 bg-transparent border-border text-foreground hover:bg-primary hover:border-primary hover:text-primary-foreground" />
          </div>
        </Carousel>

        <div className="mt-16 text-center">
          <Button asChild variant="link" className="text-primary font-default text-lg hover:text-primary/80 p-0 h-auto">
            <Link to="/ServicesPage" className="flex items-center gap-2 group">
              View Full Fleet
              <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}